import React, { useState } from 'react';
import { SelectSearch, SelectSearchOption, SelectSearchGroup } from './SelectSearch';
import { Button } from './Button';
import { Input } from './Input';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { DynamicFieldValue } from '../../automation/components/DynamicFieldValue';
import type { AvailableDataSource } from '../../automation/components/DynamicFieldValue';

export interface VariableMappingVariable {
  name: string;
  type?: string;
  required?: boolean;
  description?: string;
  defaultValue?: any;
}

export interface VariableMappingTarget {
  key: string;
  label: string;
  type?: string;
  description?: string;
}

export interface VariableMappingProps {
  // Source variables to map
  variables: VariableMappingVariable[];

  // Target options to map to (only for legacy SelectSearch mode)
  targets?: VariableMappingTarget[];

  // Initial mapping
  mapping?: Record<string, string>;

  // Callbacks
  onSave: (mapping: Record<string, string>) => void;
  onCancel: () => void;

  // UI Configuration
  title?: string;
  description?: string;
  variableLabel?: string;
  targetLabel?: string;
  targetGroupLabel?: string;
  autoMapLabel?: string;
  emptyMessage?: string;
  requiredMessage?: string;
  saving?: boolean;

  // Auto-mapping configuration
  autoMapRules?: Record<string, string[]>;
  enableAutoMap?: boolean;

  // DynamicFieldValue mode (new)
  useDynamicFieldValue?: boolean;
  availableData?: AvailableDataSource[];
  displayTransform?: (value: string) => string;

  // Read-only mode (for viewing auto-generated mappings)
  readOnly?: boolean;

  // Allow editing variable names and adding new variables
  allowEditVariableNames?: boolean;
}

export function VariableMapping({
  variables,
  targets = [],
  mapping: initialMapping = {},
  onSave,
  onCancel,
  title = 'Variable Mappings',
  description,
  variableLabel = 'Variable',
  targetLabel = 'Target',
  targetGroupLabel = 'Available Targets',
  autoMapLabel = 'Auto-map',
  emptyMessage = 'No variables to map',
  requiredMessage = 'Missing required mappings',
  saving = false,
  autoMapRules,
  enableAutoMap = true,
  useDynamicFieldValue = false,
  availableData = [],
  displayTransform,
  readOnly = false,
  allowEditVariableNames = false
}: VariableMappingProps) {
  const { t } = useTranslation();
  const colors = useThemeColors();

  // Initialize mapping based on mode
  const initializeState = () => {
    if (useDynamicFieldValue) {
      // In DynamicFieldValue mode, values are already in the correct format
      // (either {{...}} for dynamic or plain text for static)
      return { mapping: { ...initialMapping } };
    } else {
      // Legacy mode: separate dynamic mappings and static values
      const dynamicMappings: Record<string, string> = {};
      const staticMappings: Record<string, string> = {};

      Object.entries(initialMapping).forEach(([key, value]) => {
        // Check if it's a static value (not __static__ and not in targets)
        const isTargetKey = targets.some((t) => t.key === value);

        if (!isTargetKey && value !== '__static__') {
          // This is a static value
          dynamicMappings[key] = '__static__';
          staticMappings[key] = value;
        } else {
          // This is a dynamic mapping
          dynamicMappings[key] = value;
        }
      });

      return { mapping: dynamicMappings, staticValues: staticMappings };
    }
  };

  const initialState = initializeState();
  const [mapping, setMapping] = useState<Record<string, string>>(initialState.mapping);
  const [staticValues, setStaticValues] = useState<Record<string, string>>(initialState.staticValues || {});

  // Track variable names separately to allow editing without affecting mapping keys
  const [variableNames, setVariableNames] = useState<Record<string, string>>(() => {
    // Initialize with mapping keys (original name -> current display name)
    const names: Record<string, string> = {};
    Object.keys(initialState.mapping).forEach((key) => {
      names[key] = key;
    });
    return names;
  });

  // When allowEditVariableNames is true, derive variables from variable names state
  // This allows adding new variables without parent component updates
  const actualVariables = allowEditVariableNames
    ? Object.keys(variableNames).map((originalName) => ({
        name: variableNames[originalName],
        originalName: originalName, // Track original key for mapping lookups
        type: 'string',
        required: false,
        description: variables.find((v) => v.name === originalName)?.description
      }))
    : variables;

  // Get suggested mapping based on variable name
  const getSuggestedTarget = (variableName: string): string => {
    const lowerVarName = variableName.toLowerCase();

    // Try exact match first
    const exactMatch = targets.find(
      target => target.key.toLowerCase() === lowerVarName
    );
    if (exactMatch) return exactMatch.key;

    // Try custom auto-map rules if provided
    if (autoMapRules) {
      for (const [varPattern, targetPatterns] of Object.entries(autoMapRules)) {
        if (lowerVarName.includes(varPattern.toLowerCase())) {
          for (const pattern of targetPatterns) {
            const target = targets.find(
              t => t.key.toLowerCase().includes(pattern.toLowerCase())
            );
            if (target) return target.key;
          }
        }
      }
    }

    // Default common mappings
    const commonMappings: Record<string, string[]> = {
      'email': ['email', 'email_address', 'emailAddress'],
      'name': ['name', 'full_name', 'fullName', 'display_name'],
      'firstname': ['first_name', 'firstName', 'fname'],
      'lastname': ['last_name', 'lastName', 'lname'],
      'phone': ['phone', 'phone_number', 'phoneNumber'],
      'address': ['address', 'address1', 'street'],
      'city': ['city'],
      'state': ['state', 'province'],
      'zip': ['zip', 'zipcode', 'postal_code'],
      'id': ['id', 'identifier'],
      'date': ['date', 'created_at', 'updated_at'],
      'amount': ['amount', 'total', 'value'],
      'quantity': ['quantity', 'qty', 'count'],
      'status': ['status', 'state'],
      'type': ['type', 'category'],
      'description': ['description', 'desc', 'details'],
      'notes': ['notes', 'comments', 'remarks']
    };

    for (const [varPattern, targetPatterns] of Object.entries(commonMappings)) {
      if (lowerVarName.includes(varPattern)) {
        for (const pattern of targetPatterns) {
          const target = targets.find(
            t => t.key.toLowerCase().includes(pattern)
          );
          if (target) return target.key;
        }
      }
    }

    return '';
  };

  const handleMappingChange = (variableName: string, targetKey: string) => {
    if (targetKey === '__static__') {
      // If static value selected, store it as __static__ in mapping
      setMapping(prev => ({
        ...prev,
        [variableName]: '__static__'
      }));
    } else {
      // Regular dynamic mapping
      setMapping(prev => ({
        ...prev,
        [variableName]: targetKey
      }));
      // Clear static value if switching to dynamic
      setStaticValues(prev => {
        const updated = { ...prev };
        delete updated[variableName];
        return updated;
      });
    }
  };

  const handleStaticValueChange = (variableName: string, value: string) => {
    setStaticValues(prev => ({
      ...prev,
      [variableName]: value
    }));
  };

  const handleClearMapping = (variableName: string) => {
    setMapping(prev => {
      const updated = { ...prev };
      delete updated[variableName];
      return updated;
    });
    setStaticValues(prev => {
      const updated = { ...prev };
      delete updated[variableName];
      return updated;
    });
  };

  const handleAutoMap = () => {
    const autoMapping: Record<string, string> = {};
    variables.forEach(variable => {
      const suggested = getSuggestedTarget(variable.name);
      if (suggested) {
        autoMapping[variable.name] = suggested;
      }
    });
    setMapping(autoMapping);
  };

  const handleSave = () => {
    let finalMapping: Record<string, string>;

    if (useDynamicFieldValue) {
      // In DynamicFieldValue mode, mapping already contains final values
      finalMapping = { ...mapping };
    } else {
      // Legacy mode: combine dynamic mappings and static values
      finalMapping = { ...mapping };

      // Add static values to the mapping
      Object.entries(staticValues).forEach(([varName, value]) => {
        if (value) {
          finalMapping[varName] = value;
        }
      });
    }

    // When allowEditVariableNames is true, apply variable name changes
    if (allowEditVariableNames) {
      const renamedMapping: Record<string, string> = {};
      const seenNames = new Set<string>();

      // Validate and apply renames
      for (const [originalName, displayName] of Object.entries(variableNames)) {
        const trimmedName = displayName.trim();

        // Validate variable name
        if (!trimmedName) {
          Toast.show({
            type: 'error',
            text1: t('Invalid variable name'),
            text2: t('Variable names cannot be empty')
          });
          return;
        }

        // Check for duplicates
        if (seenNames.has(trimmedName)) {
          Toast.show({
            type: 'error',
            text1: t('Duplicate variable name'),
            text2: t(`Variable name "${trimmedName}" is used more than once`)
          });
          return;
        }

        seenNames.add(trimmedName);

        // Apply rename to final mapping
        if (finalMapping[originalName] !== undefined) {
          renamedMapping[trimmedName] = finalMapping[originalName];
        }
      }

      finalMapping = renamedMapping;
    }

    // Validate required variables are mapped
    const requiredVariables = variables.filter((v) => v.required);
    const missingRequired = requiredVariables.filter(
      (v) => !finalMapping[v.name]
    );

    if (missingRequired.length > 0) {
      Toast.show({
        type: 'error',
        text1: t(requiredMessage),
        text2: t('Please map all required variables: ') + missingRequired.map((v) => v.name).join(', ')
      });
      return;
    }

    onSave(finalMapping);
  };

  // Handle adding a new variable with auto-generated name
  const handleAddVariable = () => {
    // Find next available variable name
    let counter = 1;
    let varName = `newVariable${counter}`;

    while (variableNames[varName]) {
      counter++;
      varName = `newVariable${counter}`;
    }

    // Add new variable with empty mapping
    setMapping((prev) => ({
      ...prev,
      [varName]: ''
    }));

    // Add to variable names tracking
    setVariableNames((prev) => ({
      ...prev,
      [varName]: varName
    }));
  };

  // Handle updating variable name display (doesn't affect mapping until save)
  const handleVariableNameChange = (originalName: string, newDisplayName: string) => {
    setVariableNames((prev) => ({
      ...prev,
      [originalName]: newDisplayName
    }));
  };

  // Handle removing a variable
  const handleRemoveVariable = (originalName: string) => {
    setMapping((prev) => {
      const updated = { ...prev };
      delete updated[originalName];
      return updated;
    });

    setVariableNames((prev) => {
      const updated = { ...prev };
      delete updated[originalName];
      return updated;
    });

    // Also clear any static values
    setStaticValues((prev) => {
      const updated = { ...prev };
      delete updated[originalName];
      return updated;
    });
  };

  // Build target options for the combobox
  const targetGroups: SelectSearchGroup[] = [{
    key: 'targets',
    label: targetGroupLabel,
    options: targets.map(target => ({
      value: target.key,
      label: target.label,
      subtitle: target.type || target.description
    }))
  }];

  return (
    <div className="space-y-4">
      {description && (
        <div className={`p-3 rounded ${colors.classes.bgInfo}`}>
          <p className={`text-sm ${colors.classes.textInfo}`}>
            {t(description)}
          </p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h4 className={`text-sm font-medium ${colors.classes.textPrimary}`}>
          {t(title)} {readOnly && <span className={`text-xs font-normal ${colors.classes.textSecondary}`}>({t('Read-only')})</span>}
        </h4>
        {enableAutoMap && !readOnly && (
          <Button
            size="xs"
            variant="outline"
            icon={<FontAwesomeIcon icon={faWandMagicSparkles} className="w-3 h-3" />}
            onClick={handleAutoMap}
          >
            {t(autoMapLabel)}
          </Button>
        )}
      </div>

      {/* Add New Variable Button - Only for body variable mode */}
      {!readOnly && allowEditVariableNames && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddVariable}
          >
            {t('Add Variable')}
          </Button>
        </div>
      )}

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {actualVariables.map((variable, index) => {
          if (useDynamicFieldValue) {
            // New mode: use DynamicFieldValue component
            // Use index as key to prevent re-mounting when name changes
            return (
              <div
                key={`variable-${index}`}
                className={`p-4 rounded-lg border ${colors.classes.border} ${colors.classes.bgSecondary} space-y-3`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    {allowEditVariableNames ? (
                      // Editable variable name in body variable mode
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${colors.classes.textSecondary}`}>
                          {t('Variable Name')}
                        </label>
                        <Input
                          value={variable.name}
                          onChange={(e) => {
                            handleVariableNameChange(variable.originalName, e.target.value);
                          }}
                          placeholder={t('Variable name')}
                          size="sm"
                          fullWidth
                          disabled={readOnly}
                        />
                      </div>
                    ) : null}
                    <DynamicFieldValue
                      label={allowEditVariableNames ? t('Value') : variable.name}
                      value={mapping[variable.originalName || variable.name] || ''}
                      onChange={readOnly ? undefined : (value) => {
                        setMapping((prev) => ({
                          ...prev,
                          [variable.originalName || variable.name]: value
                        }));
                      }}
                      required={variable.required}
                      availableData={availableData}
                      displayTransform={displayTransform}
                      description={variable.description}
                      disabled={readOnly}
                    />
                  </div>
                  {allowEditVariableNames && !readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        handleRemoveVariable(variable.originalName);
                      }}
                      className={`${colors.classes.textError} hover:${colors.classes.bgError} mt-6`}
                      title={t('Remove variable')}
                    >
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          }

          // Legacy mode: use SelectSearch + Input
          const isStaticSelected = mapping[variable.name] === '__static__' || (!mapping[variable.name] && staticValues[variable.name]);

          return (
            <div key={variable.name} className="grid grid-cols-2 gap-4 items-start">
              <div>
                {allowEditVariableNames ? (
                  // Editable variable name in body variable mode
                  <div className="space-y-1">
                    <Input
                      value={variable.name}
                      onChange={(e) => {
                        handleRenameVariable(variable.name, e.target.value);
                      }}
                      placeholder={t('Variable name')}
                      size="sm"
                      fullWidth
                      disabled={readOnly}
                    />
                    {variable.description && (
                      <p className={`text-xs ${colors.classes.textSecondary}`}>
                        {variable.description}
                      </p>
                    )}
                  </div>
                ) : (
                  // Fixed variable name in template mode
                  <>
                    <label className={`block text-sm ${colors.classes.textPrimary}`}>
                      {variable.name}
                      {variable.required && (
                        <span className={`ml-1 ${colors.classes.textError}`}>*</span>
                      )}
                    </label>
                    {variable.type && (
                      <span className={`text-xs ${colors.classes.textSecondary}`}>
                        {t('Type')}: {variable.type}
                      </span>
                    )}
                    {variable.description && (
                      <p className={`text-xs mt-0.5 ${colors.classes.textSecondary}`}>
                        {variable.description}
                      </p>
                    )}
                    {variable.defaultValue !== undefined && (
                      <p className={`text-xs mt-0.5 ${colors.classes.textSecondary}`}>
                        {t('Default')}: {String(variable.defaultValue)}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="space-y-2">
                <SelectSearch
                  mode="static"
                  groups={targetGroups}
                  value={isStaticSelected ? '__static__' : (mapping[variable.name] || null)}
                  onChange={(value) => {
                    if (value === '') {
                      handleClearMapping(variable.name);
                    } else {
                      handleMappingChange(variable.name, value as string);
                    }
                  }}
                  placeholder={t('Select target...')}
                  emptyMessage={t('No targets available')}
                  size="sm"
                  fullWidth
                  clearable
                />
                {isStaticSelected && (
                  <Input
                    value={staticValues[variable.name] || ''}
                    onChange={(e) => handleStaticValueChange(variable.name, e.target.value)}
                    placeholder={t('Enter static value...')}
                    size="sm"
                    fullWidth
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {actualVariables.length === 0 && (
        <div className={`text-center py-8 ${colors.classes.textSecondary}`}>
          {t(emptyMessage)}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          {readOnly ? t('Close') : t('Cancel')}
        </Button>
        {!readOnly && (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('Saving...') : t('Save Mapping')}
          </Button>
        )}
      </div>
    </div>
  );
}